import React, { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { useExercisePlanStore } from '@/stores/exercisePlanStore'
import {
  Users,
  Search,
  Check,
  X,
  User,
  Calendar,
  Target,
  Clock
} from 'lucide-react'
import UserAvatar from '@/components/UserAvatar'

const AssignClientModal = ({ isOpen, onClose, plan }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedClients, setSelectedClients] = useState([])

  const { assignPlanToClient, unassignPlanFromClient } = useExercisePlanStore()

  // Mock client data - in real app, this would come from a clients store
  const mockClients = [
    {
      id: 'client-1',
      name: 'John Smith',
      email: 'john.smith@email.com',
      avatar: null,
      lastActive: '2024-01-15',
      currentPlan: 'Full Body Strength Training',
      goals: ['Weight Loss', 'Muscle Building']
    },
    {
      id: 'client-2',
      name: 'Sarah Johnson',
      email: 'sarah.j@email.com',
      avatar: null,
      lastActive: '2024-01-14',
      currentPlan: null,
      goals: ['Endurance', 'General Fitness']
    },
    {
      id: 'client-3',
      name: 'Mike Davis',
      email: 'mike.davis@email.com',
      avatar: null,
      lastActive: '2024-01-13',
      currentPlan: 'HIIT Cardio Blast',
      goals: ['Weight Loss', 'Cardio Health']
    },
    {
      id: 'client-4',
      name: 'Emily Wilson',
      email: 'emily.w@email.com',
      avatar: null,
      lastActive: '2024-01-12',
      currentPlan: null,
      goals: ['Strength', 'Flexibility']
    },
    {
      id: 'client-5',
      name: 'David Brown',
      email: 'david.brown@email.com',
      avatar: null,
      lastActive: '2024-01-11',
      currentPlan: 'Full Body Strength Training',
      goals: ['Muscle Building', 'Strength']
    }
  ]

  const filteredClients = mockClients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleClientToggle = (clientId) => {
    setSelectedClients(prev => {
      const isSelected = prev.includes(clientId)
      if (isSelected) {
        return prev.filter(id => id !== clientId)
      } else {
        return [...prev, clientId]
      }
    })
  }

  const handleAssignPlan = () => {
    if (!plan || selectedClients.length === 0) return

    selectedClients.forEach(clientId => {
      assignPlanToClient(plan.id, clientId)
    })

    setSelectedClients([])
    setSearchTerm('')
    onClose()
  }

  const handleUnassignClient = (clientId) => {
    if (!plan) return
    unassignPlanFromClient(plan.id, clientId)
  }

  const handleClose = () => {
    setSelectedClients([])
    setSearchTerm('')
    onClose()
  }

  const isClientAssigned = (clientId) => {
    return plan?.assignedClients.includes(clientId)
  }

  if (!isOpen || !plan) return null

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Assign Plan to Clients
          </DialogTitle>
          <p className="text-sm text-slate-600">
            Assign "{plan.name}" to your clients
          </p>
        </DialogHeader>

        {/* Plan Summary */}
        <div className="bg-blue-50 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-blue-900">{plan.name}</h3>
              <p className="text-sm text-blue-700">{plan.description}</p>
            </div>
            <div className="flex items-center gap-4 text-sm text-blue-700">
              <div className="flex items-center">
                <Clock className="h-4 w-4 mr-1" />
                {plan.duration} min
              </div>
              <div className="flex items-center">
                <Target className="h-4 w-4 mr-1" />
                {plan.sections.reduce((total, section) => total + section.exercises.length, 0)} exercises
              </div>
            </div>
          </div>
        </div>

        {/* Currently Assigned Clients */}
        {plan.assignedClients.length > 0 && (
          <div className="mb-4">
            <h4 className="font-medium text-slate-900 mb-2">Currently Assigned ({plan.assignedClients.length})</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {plan.assignedClients.map(clientId => {
                const client = mockClients.find(c => c.id === clientId)
                if (!client) return null
                
                return (
                  <div key={clientId} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <UserAvatar
                        name={client.name}
                        email={client.email}
                        className="w-8 h-8"
                      />
                      <div>
                        <div className="font-medium text-green-900">{client.name}</div>
                        <div className="text-sm text-green-700">{client.email}</div>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleUnassignClient(clientId)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Client List */}
        <div className="flex-1 overflow-y-auto">
          {filteredClients.length === 0 ? (
            <div className="text-center py-8">
              <User className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No clients found</h3>
              <p className="text-slate-600">Try adjusting your search terms</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredClients.map(client => {
                const isAssigned = isClientAssigned(client.id)
                const isSelected = selectedClients.includes(client.id)
                
                return (
                  <Card 
                    key={client.id}
                    className={`cursor-pointer transition-all duration-200 ${
                      isAssigned 
                        ? 'bg-slate-50 border-slate-300' 
                        : isSelected 
                          ? 'ring-2 ring-blue-500 bg-blue-50' 
                          : 'hover:bg-slate-50'
                    }`}
                    onClick={() => !isAssigned && handleClientToggle(client.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <UserAvatar
                            name={client.name}
                            email={client.email}
                            className="w-10 h-10"
                          />
                          
                          <div>
                            <div className="font-medium text-slate-900">{client.name}</div>
                            <div className="text-sm text-slate-600">{client.email}</div>
                            {client.currentPlan && (
                              <div className="text-xs text-slate-500 mt-1">
                                Current: {client.currentPlan}
                              </div>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              {client.goals.slice(0, 2).map(goal => (
                                <span key={goal} className="px-2 py-1 bg-slate-100 text-xs text-slate-600 rounded">
                                  {goal}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <div className="text-right text-sm text-slate-500">
                            <div className="flex items-center">
                              <Calendar className="h-3 w-3 mr-1" />
                              {new Date(client.lastActive).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                            </div>
                          </div>
                          
                          {isAssigned ? (
                            <div className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                              Assigned
                            </div>
                          ) : isSelected ? (
                            <div className="bg-blue-500 text-white rounded-full p-1">
                              <Check className="h-4 w-4" />
                            </div>
                          ) : (
                            <div className="w-6 h-6 border-2 border-slate-300 rounded-full"></div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="border-t border-slate-200 pt-4 flex items-center justify-between">
          <div className="text-sm text-slate-600">
            {selectedClients.length} client{selectedClients.length !== 1 ? 's' : ''} selected
          </div>
          
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            
            {selectedClients.length > 0 && (
              <Button variant="fitness" onClick={handleAssignPlan}>
                <Users className="h-4 w-4 mr-2" />
                Assign to {selectedClients.length} Client{selectedClients.length !== 1 ? 's' : ''}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default AssignClientModal